import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableMenuItem } from './SortableMenuItem';
import { NavItem } from '@/shared/types/navigation';
import { useTranslation } from 'react-i18next';

interface MenuOrderSettingsProps {
  items: NavItem[];
  onSave: (items: NavItem[]) => void;
}

export const MenuOrderSettings: React.FC<MenuOrderSettingsProps> = ({ items, onSave }) => {
  const { t } = useTranslation();
  const [menuItems, setMenuItems] = React.useState(items);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setMenuItems((items) => {
        const oldIndex = items.findIndex((item) => item.name === active.id);
        const newIndex = items.findIndex((item) => item.name === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);

        // Save immediately after drag
        onSave(newItems);
        return newItems;
      });
    }
  };

  return (
    
    <div className="flex flex-col gap-6" data-testid="menu-order-settings">
      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-testid="menu-order-title">{t("settings.menu_order.title")}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="menu-order-description">
          {t("settings.menu_order.description")}
        </p>
      </div>

      <div className="flex flex-col gap-2 touch-none" data-testid="menu-items-container">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={menuItems.map(item => item.name)}
            strategy={verticalListSortingStrategy}
          >
            {menuItems.map((item) => (
              <SortableMenuItem key={item.name} item={item} />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};
